import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../../CustomHeadlessTable/CustomTable";
import {
  getTransportPackagingOutgoingListLCA,
  addTransportPackagingOutgoingLCA,
  updateTransportPackagingOutgoingLCA,
  deleteTransportPackagingOutgoingLCA,
} from "../../../../../../../apis/Product/TransportPackagingApi";
import { getTransportPackagingMaterialDropdown } from "../../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../../CustomHeadlessTable/components/CustomInputComponent";
import { IconButton, Tooltip } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import InfoIcon from "components/InfoIcon";
import TipBox from "components/TipBox";
import { TableCompareTrigger } from "../../../../components/lcaComparison";

const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  {
    name: "Photochemical ozone formation",
    unit: "kg NMVOC-eq",
    key: "smog",
  },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  {
    name: "Eutrophication, marine",
    unit: "kg N-eq",
    key: "eutrophication_marine",
  },
  {
    name: "Human toxicity, cancer effects",
    unit: "CTUh",
    key: "carcinogenics",
  },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  {
    name: "Particulate matter",
    unit: "disease incidence",
    key: "respiratory_effects",
  },
  {
    name: "Ecotoxicity, freshwater",
    unit: "CTUe",
    key: "ecotoxicity",
  },
  {
    name: "Resource use, energy carriers",
    unit: "MJ",
    key: "fossil_fuel_depletion",
  },
  { name: "Land use", unit: "Pt", key: "land_use" },
  {
    name: "Water use",
    unit: "m³ world-eq deprived",
    key: "water_use",
  },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const TransportPackagingOutgoingTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});
  const [materialDropdown, setMaterialDropdown] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] =
    useState(0);

  useEffect(() => {
    getTransportPackagingMaterialDropdown({
      flow: "31.2b", // Flow 31.2b - Transport packaging outgoing
      setDropdown: setMaterialDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (productId) {
      getTransportPackagingOutgoingListLCA({
        productId,
        setObjects: setData,
      });
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialCode =
          typeof row.material === "string"
            ? row.material
            : row.material?.value ||
              row.material ||
              row.input ||
              (typeof row.input === "string" ? row.input : row.input?.value);
        if (materialCode) {
          const selectedMaterial = materialDropdown.find(
            (option) =>
              !option.isCategory &&
              (option.code === materialCode ||
                option.value === materialCode ||
                option.name === materialCode)
          );
          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, materialDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      material: {
        targetField: "qty",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedMaterial = dropdownData?.find(
            (option) => option.value === selectedValue
          );
          if (selectedMaterial) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedMaterial.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedMaterial.unit,
              }));
            }
          }
        },
        dropdownDataKey: "material",
      },
    }),
    []
  );

  const resolveMaterialCode = (value) =>
    value?.value || value || value?.input?.value || value?.input;

  const findMaterial = (materialValue) =>
    materialDropdown?.find(
      (option) =>
        !option.isCategory &&
        (option.value === materialValue ||
          option.code === materialValue ||
          option.name === materialValue)
    );

  const handleEdit = useCallback(
    (editedItem) => {
      const materialValue = resolveMaterialCode(editedItem.material);
      const foundMaterial = findMaterial(materialValue);
      const materialCode = foundMaterial?.code || materialValue;
      const product_code = foundMaterial?.code || editedItem.product_code;

      updateTransportPackagingOutgoingLCA({
        productId,
        packagingId: editedItem.id,
        material: materialCode,
        qty: editedItem.qty,
        supplier: editedItem.supplier,
        product_code: product_code,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [productId, data, setData, materialDropdown]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const materialValue = resolveMaterialCode(newItem.material);
      const foundMaterial = findMaterial(materialValue);
      const materialCode = foundMaterial?.code || materialValue;
      const product_code = foundMaterial?.code || "";

      addTransportPackagingOutgoingLCA({
        productId,
        material: materialCode,
        qty: newItem.qty,
        supplier: newItem.supplier,
        product_code: product_code,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData, materialDropdown]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteTransportPackagingOutgoingLCA({
        productId,
        packagingId: itemToDelete.id,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData]
  );

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev > 0 ? prev - 1 : IMPACT_CATEGORIES.length - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev < IMPACT_CATEGORIES.length - 1 ? prev + 1 : 0
    );
  }, []);

  const columns = useMemo(() => {
    const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
    const impactCategoryAccessor = currentImpactCategory.key;

    return [
      {
        accessor: "material",
        header: `${t("MATERIAL") || "Material"}*`,
        type: "dropdown",
        isCategory: true,
        dropdownData: materialDropdown,
        disableSortBy: true,
        unit: (rowId, rowData) => {
          const materialCode =
            typeof rowData?.material === "string"
              ? rowData.material
              : rowData?.material?.value ||
                rowData?.material ||
                rowData?.input ||
                (typeof rowData?.input === "string"
                  ? rowData.input
                  : rowData?.input?.value);
          if (materialCode) {
            const foundMaterial = materialDropdown.find(
              (option) =>
                !option.isCategory &&
                (option.code === materialCode ||
                  option.value === materialCode ||
                  option.name === materialCode)
            );
            return foundMaterial?.unit || unitMaterials[rowId] || "unit";
          }
          return unitMaterials[rowId] || "unit";
        },
      },
      {
        accessor: "qty",
        header: `${t("QTY_FUNC_UNIT") || "Quantity per functional unit"}*`,
        type: "number",
        tipHeader: "Tip",
        tipBody:
          'Some selected items do not have units; "unit" will be shown as a placeholder.',
        disableSortBy: true,
        placeholder: t("USER_TYPES") || "user types...",
        width: "180px",
        unit: (rowId, rowData) => {
          const materialCode =
            typeof rowData?.material === "string"
              ? rowData.material
              : rowData?.material?.value ||
                rowData?.material ||
                rowData?.input ||
                (typeof rowData?.input === "string"
                  ? rowData.input
                  : rowData?.input?.value);
          if (materialCode) {
            const foundMaterial = materialDropdown.find(
              (option) =>
                !option.isCategory &&
                (option.code === materialCode ||
                  option.value === materialCode ||
                  option.name === materialCode)
            );
            return foundMaterial?.unit || unitMaterials[rowId] || "unit";
          }
          return unitMaterials[rowId] || "unit";
        },
      },
      {
        accessor: "supplier",
        header: t("SUPPLIER") || "Supplier",
        type: "text",
        disableSortBy: true,
        placeholder: t("USER_TYPES") || "user types...",
        width: "150px",
      },
      {
        accessor: impactCategoryAccessor,
        header: () => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <Tooltip title="Previous impact category">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreviousImpactCategory();
                  }}
                  style={{ padding: "4px" }}
                >
                  <KeyboardArrowLeft fontSize="small" />
                </IconButton>
              </Tooltip>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <span>
                    {currentCategory.name}{" "}
                    {currentCategory.unit && `(${currentCategory.unit})`}
                  </span>
                  <TipBox
                    title={currentCategory.name}
                    message={`Impact category: ${currentCategory.name}. Unit: ${
                      currentCategory.unit || "N/A"
                    }`}
                  >
                    <IconButton size="small" style={{ padding: "2px" }}>
                      <InfoIcon />
                    </IconButton>
                  </TipBox>
                </div>
              </div>
              <Tooltip title="Next impact category">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextImpactCategory();
                  }}
                  style={{ padding: "4px" }}
                >
                  <KeyboardArrowRight fontSize="small" />
                </IconButton>
              </Tooltip>
              <TableCompareTrigger />
            </div>
          );
        },
        type: "custom",
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        optional: true,
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ];
  }, [
    materialDropdown,
    t,
    unitMaterials,
    currentImpactCategoryIndex,
    handlePreviousImpactCategory,
    handleNextImpactCategory,
  ]);

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: {
        label: `Total ${currentImpactCategory.name}`,
      },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      material: materialDropdown,
    }),
    [materialDropdown]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({ [impactCategoryAccessor]: CustomInputComponent }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default TransportPackagingOutgoingTable;
